<template>
  <AccountMenu
    :full-name="name"
    :display-name="firstName"
    :avatar-image-url="profileImage"
    :menu-items="dropdownMenuItems"
    :aria-label="ariaLabel"
    :overlay-position="overlayPosition"
    :size="size"
    :caret="caret"
    :trigger-width="triggerWidth"
    :trigger-layout="triggerLayout"
    :overlay-align="overlayAlign"
    :trigger-tab-index="triggerTabIndex"
    menu-aria-label="Primary navigation menu"
  >
    <template #header>
      <div class="flex">
        <Avatar :name="name" :imageUrl="profileImage" size="large" />
        <div class="flex flex-col ml-16">
          <p class="text-h4">{{ formattedName }}</p>
          <a v-if="accountType === 'Basic'" class="text-body text-primary" href="/upgrade/pricing">Upgrade to Premium</a>
          <p v-else class="text-body">{{ memberTypeText }}</p>
        </div>
      </div>
    </template>
  </AccountMenu>
</template>

<script lang="ts">
import type { MenuItem } from '@/components/menu_link_list/MenuLinkListTypes';
import AccountMenu from '@/components/account_menu/AccountMenu.vue';
import Avatar from '@/components/avatar/Avatar.vue';
import ProfileIcon from '@/icons/global_nav/ProfileIcon.vue';
import FavoritesIcon from '@/icons/global_nav/FavoritesIcon.vue';
import HelpIcon from '@/icons/global_nav/HelpIcon.vue';
import LogOutIcon from '@/icons/global_nav/LogOutIcon.vue';
import PremiumUpgradeIcon from '@/icons/global_nav/PremiumUpgradeIcon.vue';
import TrustAndSafetyIcon from '@/graphics/icons/TrustAndSafetyIcon.vue';

export default {
  name: 'GlobalNavDropdown',
  components: { AccountMenu, Avatar },
  props: {
    name: {
      type: String,
      required: true,
    },
    firstName: {
      type: String,
      default: '',
    },
    lastName: {
      type: String,
      default: '',
    },
    profileImage: {
      type: String,
      default: '',
    },
    accountType: {
      type: String,
      default: '',
    },
    isSimpleHeader: {
      type: Boolean,
      default: false,
    },
    overlayPosition: {
      type: String,
      default: 'right',
      validator: (value: string) => ['dropdown', 'dropup', 'top', 'bottom', 'right'].includes(value),
    },
    size: {
      type: String,
      default: 'small',
    },
    triggerWidth: {
      type: Number,
      default: 0,
    },
    caret: {
      type: Boolean,
      default: true,
    },
    triggerLayout: {
      type: String,
      default: 'column',
    },
    overlayAlign: {
      type: String,
      default: 'right',
    },
    triggerTabIndex: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    formattedName(): string {
      return `${this.firstName} ${this.lastName ? `${this.lastName[0].toUpperCase()}.` : ''}`.trim();
    },
    memberTypeText(): string {
      return this.accountType === 'Basic' ? 'Upgrade to Premium' : `${this.accountType} member`;
    },
    ariaLabel(): string {
      return this.firstName ? `Account Menu. You are logged in as ${this.firstName}.` : 'Account menu';
    },
    dropdownMenuItems(): MenuItem[] {
      return [
        {
          icon: ProfileIcon,
          label: 'Account',
          href: '/account/settings',
          visible: !this.isSimpleHeader,
        },
        {
          icon: PremiumUpgradeIcon,
          label: 'Premium pricing',
          href: '/upgrade/pricing',
          visible: this.accountType === 'Basic' && !this.isSimpleHeader,
        },
        {
          icon: FavoritesIcon,
          label: 'Favorites',
          href: '/account/seekers/favorites',
          visible: !this.isSimpleHeader,
        },
        {
          icon: TrustAndSafetyIcon,
          label: 'Trust & Safety',
          href: 'https://www.sittercity.com/trust-and-safety',
          target: '_blank',
          visible: true,
        },
        {
          icon: HelpIcon,
          label: 'Help',
          href: 'https://support.sittercity.com/hc/en-us',
          target: '_blank',
          visible: true,
        },
        {
          icon: LogOutIcon,
          label: 'Log out',
          href: '/logout',
          visible: true,
        },
      ];
    },
  },
};
</script>
